import { useEffect, useState } from 'react'

import { useToasts } from '@/hooks/useToasts'

import {
  useCreateTailgateConnMutation,
  useDeviceAssocationOptionsQuery,
  useModifyTailgateConnMutation,
} from '@/graphql/generated/hooks'
import { DeviceAssocationOptionsQuery } from '@/graphql/generated/operations'
import { DeviceType } from '@/graphql/generated/schemas'
import { OptionI } from '@/utils/forms/optionBuilders'

import { EditDeviceFieldsI, Options } from '../types/types'
import {
  areOptionsEqual,
  filterDeviceOptions,
  getAllDeviceConnections,
  getCameraIdsForMutation,
  getDeviceConnection,
  getDeviceTypeFilter,
  getRemovedDeviceConnections,
  getSelectedCameras,
  getSelectedDevices,
  isDeviceAlreadyAdded,
} from '../utils/utils'

interface UseAssociateDeviceIProps {
  deviceData: EditDeviceFieldsI
}

export const useAssociateDevice = ({
  deviceData,
}: UseAssociateDeviceIProps) => {
  const deviceId = deviceData?.id
  const deviceType = deviceData?.type
  const { showError, showSuccess } = useToasts()
  const [isAssociateDeviceLoading, setAssociateDeviceLoading] = useState(false)
  const [selectedDevices, setSelectedDevices] = useState<Options>([])
  const [associatedDeviceOptions, setAssociatedDeviceOptions] =
    useState<Options>([])
  // Used for validating if the devices are been updated. I.e Enabling/Disabling the Save button
  const [originalDevices, setOriginalDevices] = useState<Options>([])
  const [isDirty, setIsDirty] = useState(false)
  const deviceTypes = getDeviceTypeFilter(deviceType)

  const handleSavedDevices = (data: DeviceAssocationOptionsQuery) => {
    if (deviceType === DeviceType.Camera) {
      setOriginalDevices(getSelectedDevices(data, deviceId))
    } else {
      setOriginalDevices(getSelectedCameras(data, deviceId))
    }
  }

  const { data } = useDeviceAssocationOptionsQuery({
    variables: {
      filter: {
        floorIdIn: [deviceData?.floorId],
        typeIn: deviceTypes,
      },
      id: deviceId,
    },
    onCompleted: (data) => {
      if (deviceType === DeviceType.Camera) {
        setSelectedDevices(getSelectedDevices(data, deviceId))
      } else {
        setSelectedDevices(getSelectedCameras(data, deviceId))
      }
      // Only set on initial load
      if (originalDevices.length === 0) {
        handleSavedDevices(data)
      }
    },
  })

  const [createTailgateConnection, { loading: isCreating }] =
    useCreateTailgateConnMutation({
      refetchQueries: ['DeviceAssocationOptions'],
    })
  const [modifyTailgateConnection, { loading: isUpdating }] =
    useModifyTailgateConnMutation({
      refetchQueries: ['DeviceAssocationOptions'],
    })

  const handleDeviceAssociation = async (): Promise<void> => {
    try {
      setAssociateDeviceLoading(true)
      const connection = getDeviceConnection(data, deviceId)
      // If device already has a connection, add the new connection
      if (connection) {
        const connectionId = connection?.id
        const { cameraIdsToAdd, cameraIdsToRemove } = getCameraIdsForMutation(
          data,
          selectedDevices
        )
        await modifyTailgateConnection({
          variables: {
            input: {
              id: connectionId,
              cameraIdsToAdd,
              cameraIdsToRemove,
            },
          },
        })
      } else {
        // If no existing connection, create a new one
        const cameraIds = selectedDevices.map((device) => device.value)
        await createTailgateConnection({
          variables: {
            // Note doorId in this instance represents the device id. Can be a door or alarm
            input: { cameraIds: cameraIds, doorId: deviceId },
          },
        })
      }
      showSuccess(
        `Device Association updated. ${deviceData.name} associated with ${selectedDevices?.length} devices`
      )

      setIsDirty(false)
      setOriginalDevices(selectedDevices)
      setAssociateDeviceLoading(false)
    } catch {
      setAssociateDeviceLoading(false)
      showError('Error associating devices')
    }
  }

  const handleCameraAssociation = async (): Promise<void> => {
    try {
      setAssociateDeviceLoading(true)
      await Promise.all(
        selectedDevices.map(async (deviceOption) => {
          const deviceConnections = getAllDeviceConnections(
            data,
            selectedDevices
          )
          const connection = deviceConnections.find(
            (c) => c.door.id === deviceOption.value
          )
          if (connection) {
            // Check that device is not already added(i.e previously saved device)
            if (!isDeviceAlreadyAdded(data, deviceOption.value)) {
              await modifyTailgateConnection({
                variables: {
                  input: {
                    id: connection.id,
                    cameraIdsToAdd: [deviceId],
                    cameraIdsToRemove: [],
                  },
                },
              })
            }
          } else {
            // If no connection create a new one
            await createTailgateConnection({
              variables: {
                // Note doorId in this instance represents the device id. Can be a door or alarm
                input: { cameraIds: [deviceId], doorId: deviceOption.value },
              },
            })
          }
        })
      )

      // DELETE any removed devices from the connection
      const connectionsToRemove = getRemovedDeviceConnections(
        data,
        selectedDevices
      )
      await Promise.all(
        connectionsToRemove.map(async (con) => {
          await modifyTailgateConnection({
            variables: {
              input: {
                id: con.id,
                cameraIdsToAdd: [],
                cameraIdsToRemove: [deviceId],
              },
            },
          })
        })
      )

      showSuccess(
        `Device Association updated. ${deviceData.name} associated with ${selectedDevices?.length} devices`
      )

      setIsDirty(false)
      setOriginalDevices(selectedDevices)
      setAssociateDeviceLoading(false)
    } catch {
      setAssociateDeviceLoading(false)
      showError('Error associating devices')
    }
  }

  const handleAssociation = async (): Promise<void> => {
    if (deviceType === DeviceType.Camera) {
      await handleCameraAssociation()
    } else {
      await handleDeviceAssociation()
    }
  }

  const removeSelectedDeviceOption = (deviceOption: OptionI) => {
    const updatedDevices = selectedDevices.filter(
      (device) => device.value !== deviceOption.value
    )
    setSelectedDevices(updatedDevices)
    // Add device back in dropdown options
    setAssociatedDeviceOptions([...associatedDeviceOptions, deviceOption])
    setIsDirty(!areOptionsEqual(updatedDevices, originalDevices))
  }

  const addSelectedDeviceOption = (deviceOption: OptionI) => {
    const updatedDevices = [...selectedDevices, deviceOption]
    setSelectedDevices(updatedDevices)
    const options = filterDeviceOptions(data, updatedDevices)
    // Remove device in dropdown options
    setAssociatedDeviceOptions(options)
    setIsDirty(!areOptionsEqual(updatedDevices, originalDevices))
  }

  useEffect(() => {
    if (data) {
      const options = filterDeviceOptions(data, selectedDevices)
      setAssociatedDeviceOptions(options)
    }
  }, [data])

  return {
    isDirty,
    data,
    addSelectedDeviceOption,
    handleAssociation,
    removeSelectedDeviceOption,
    associatedDeviceOptions,
    isAssociateDeviceLoading,
    isLoading: isCreating || isUpdating || isAssociateDeviceLoading,
    selectedDevices,
  }
}
